import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TargetPicker } from "@/components/bulk/TargetPicker";
import { Clock, Cpu, Truck, Search, Check, Loader2, ArrowLeft } from "lucide-react";

// WAITING DATAPLATES — an engine's plate with no truck or engine part to go on yet (his legacy
// engines). It waits here, and the moment an engine part with that ESN turns up it is OFFERED —
// never attached by itself.
const Engine = ({ e, onDone }) => {
  const [picker, setPicker] = useState(null);
  const [busy, setBusy] = useState(false);
  const attach = async (t) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dataplates/waiting/${encodeURIComponent(e.serial)}/attach`, { target: t.kind, id: t.id });
      toast.success(data.message || "Attached");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`waiting-engine-${e.serial}`} className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Cpu className="w-4 h-4 text-slate-500" />
        <span data-testid={`waiting-esn-${e.serial}`} className="font-mono text-sm font-bold text-slate-900">ESN {e.serial}</span>
        <span data-testid={`waiting-files-${e.serial}`} className="text-[11px] text-slate-500">{e.plates.map((p) => p.plate_name).join(" · ")} · from {e.files.join(", ")}</span>
      </div>
      {e.offers.length > 0 ? (
        <div className="space-y-1.5">
          <p data-testid={`waiting-turned-up-${e.serial}`} className="text-[12px] font-semibold text-emerald-800">This engine has turned up — attach its plate?</p>
          {e.offers.map((o, i) => (
            <div key={`${o.kind}-${o.id}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
              {o.kind === "part" ? <Cpu className="w-4 h-4 text-emerald-700" /> : <Truck className="w-4 h-4 text-emerald-700" />}
              <span data-testid={`waiting-offer-${e.serial}-${i}`} className="text-sm text-slate-800 flex-1">{o.kind === "part" ? "Engine part" : "Truck"} <b>{o.label}</b> <span className="text-[11px] text-slate-500">— {o.why}</span></span>
              <Button size="sm" data-testid={`waiting-attach-offer-${e.serial}-${i}`} disabled={busy} onClick={() => attach(o)}
                className="h-8 rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-xs">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}Attach here
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p data-testid={`waiting-no-offer-${e.serial}`} className="text-[12px] text-slate-500">No truck or engine part carries this ESN yet. It is offered the moment one does.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" data-testid={`waiting-pick-part-${e.serial}`} onClick={() => setPicker("part")} className="h-8 rounded-lg text-xs"><Search className="w-3.5 h-3.5 mr-1" />Pick an engine part</Button>
        <Button size="sm" variant="outline" data-testid={`waiting-pick-truck-${e.serial}`} onClick={() => setPicker("truck")} className="h-8 rounded-lg text-xs"><Search className="w-3.5 h-3.5 mr-1" />Pick a truck</Button>
      </div>
      <TargetPicker open={!!picker} kind={picker} onClose={() => setPicker(null)} onPick={(t) => { setPicker(null); attach(t); }} />
    </div>
  );
};

export default function WaitingDataplatesPage() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/dataplates/waiting").then(({ data }) => setData(data)).catch(() => setData({ engines: [], total: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="waiting-dataplates-page" className="max-w-4xl mx-auto space-y-5">
      <div className="space-y-1">
        <div><Link to="/catch-up" data-testid="waiting-back-catch-up" className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />Catch-up tools</Link></div>
        <h1 className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-2"><Clock className="w-6 h-6 text-sky-700" />Waiting Dataplates</h1>
        <p className="text-sm text-slate-600 mt-1">Engine plates with nowhere to go yet. Each one is offered when an engine part or truck with the same ESN turns up — you press Attach.</p>
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
        <>
          <p data-testid="waiting-count" className="text-sm text-slate-700"><b>{data.total}</b> engine{data.total === 1 ? "" : "s"} waiting</p>
          {data.engines.length === 0 && <p data-testid="waiting-empty" className="text-sm text-slate-500">Nothing is waiting. A plate goes here from Bulk Upload when no truck or engine part carries its ESN yet.</p>}
          {data.engines.map((e) => <Engine key={e.serial} e={e} onDone={load} />)}
        </>
      )}
    </div>
  );
}
